import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import { START_DATE, useDatepicker, useMonth } from '@datepicker-react/hooks';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { isUnavailableDate } from 'utils/DateFormats';
import DatepickerContext from './DatepickerContext';
import Month from './Month';
function Datepicker({
  selected,
  onChange,
  minBookingDate = new Date(),
  minBookingDays = 3,
  range = false,
  isSimpleDatePicker = false,
  unavailableDaysOfWeek,
  futurePricingAvailability,
  unavailableDates,
  locale = 'en-US'
}) {
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  const startDate = range ? selected?.[0] || null : selected;
  const endDate = range ? selected?.[1] || null : null;
  function handleDateChange(data) {
    if (!range) {
      onChange(data.startDate);
      return;
    }
    if (isSimpleDatePicker) {
      onChange([data.startDate, data.endDate]);
      return;
    }
    if (!data.focusedInput) {
      setFocusedInput(START_DATE);
    } else {
      setFocusedInput(data.focusedInput);
    }
    onChange([data.startDate, data.endDate]);
  }
  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths,
    goToNextYear,
    goToPreviousYear
  } = useDatepicker({
    focusedInput,
    startDate,
    endDate,
    minBookingDays: range ? minBookingDays : undefined,
    onDatesChange: handleDateChange,
    minBookingDate,
    numberOfMonths: 1,
    initialVisibleMonth: (range ? selected?.[0] : selected) ?? minBookingDate
  });
  const currentMonth = activeMonths[0];
  const monthLabelFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric'
  });
  const {
    monthLabel
  } = useMonth({
    year: currentMonth.year,
    month: currentMonth.month,
    monthLabelFormat: date => monthLabelFormatter.format(date)
  });
  return <DatepickerContext.Provider value={{
    startDate,
    endDate,
    range,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked: date_0 => isUnavailableDate(date_0, unavailableDaysOfWeek, futurePricingAvailability, unavailableDates),
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    futurePricingAvailability
  }}>
      <div className="p-4">
        <div className="flex gap-4 justify-between typography-body-1 text-neutral-700">
          <div className="flex flex-1 justify-between items-center" data-testid="month-navigation">
            <button type="button" onClick={goToPreviousMonths}>
              <NavArrowLeft ariaLabel="Previous month" size="large" />
            </button>
            {monthLabel.split(' ')[0]}
            <button type="button" onClick={goToNextMonths}>
              <NavArrowRight ariaLabel="Next month" size="large" />
            </button>
          </div>
          <div className="flex flex-1 justify-between items-center" data-testid="year-navigation">
            <button type="button" onClick={() => goToPreviousYear(1)}>
              <NavArrowLeft ariaLabel="Previous year" size="large" />
            </button>
            {monthLabel.split(' ')[1]}
            <button type="button" onClick={() => goToNextYear(1)}>
              <NavArrowRight ariaLabel="Next year" size="large" />
            </button>
          </div>
        </div>
        <div className="mt-4 space-x-2">
          {activeMonths.map(month => <Month key={`${month.year}-${month.month}`} year={month.year} month={month.month} firstDayOfWeek={firstDayOfWeek} locale={locale} />)}
        </div>
      </div>
    </DatepickerContext.Provider>;
}
Datepicker.propTypes = {
  selected: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.instanceOf(Date)), PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func,
  minBookingDate: PropTypes.instanceOf(Date),
  minBookingDays: PropTypes.number,
  isSimpleDatePicker: PropTypes.bool,
  unavailableDaysOfWeek: PropTypes.array,
  futurePricingAvailability: PropTypes.array,
  unavailableDates: PropTypes.array,
  locale: PropTypes.string
};
export { Datepicker };